import { Text } from '@chakra-ui/react'
import { memo, useMemo, useState } from 'react'
import { useMembers } from '../../../hooks/useOrganization'
import { capitalizeWords, replaceUnderscore } from '../../../utils/functions/formatters'
import SelectAsInput from './SelectAsInput'

const userQueryParams = {
  page: 1,
  fields: 'id,name,country,logo',
  page_size: 15,
  ordering: 'name',
}

interface Item {
  id: string
  name: string
  image: string | undefined | null
}

interface RawItem {
  id: string
  name: string
  logo: string | undefined | null
}

const FormMemberSelect = ({
  value,
  onAdd,
  onRemove,
  spliceName,
  name,
  labelOverride,
  hideLabel,
  isDisabled,
}: {
  value: Item | null
  onAdd: (item: Item) => void
  onRemove?: () => void
  spliceName?: boolean
  name: string
  labelOverride?: string
  // Let the parent own the label (e.g. when a mode toggle sits beside it).
  hideLabel?: boolean
  isDisabled?: boolean
}) => {
  const [memberSearch, setMemberSearch] = useState('')

  const brands = useMembers({ search: memberSearch, ...userQueryParams }, memberSearch.length > 1)

  const options = useMemo(() => {
    const searchResults =
      brands.data?.data.map((item: RawItem) => ({
        ...item,
        image: item.logo,
      })) || []

    if (value && !searchResults.find((item) => item.id === value.id)) {
      return [value, ...searchResults]
    }

    return searchResults
  }, [brands.data?.data, value])

  return (
    <>
      {!hideLabel && (
        <Text color="neutral.900" fontWeight="400" textStyle="b-sm" mb="8px">
          {labelOverride ?? capitalizeWords(name)}
        </Text>
      )}
      <SelectAsInput
        isDisabled={isDisabled}
        h="40px"
        value={value}
        w="100%"
        onChange={(v) => {
          if (v)
            onAdd({
              ...v,
              name: spliceName ? v.name.slice(0, -3) : v.name,
            } as Item)
          if (!v && onRemove) onRemove()
        }}
        search={memberSearch}
        onSearchChange={(v) => setMemberSearch(v)}
        options={options}
        isLoading={brands.isFetching}
        placeholder={`Search for a ${replaceUnderscore(name)}`}
        fontSize="12px"
      />
    </>
  )
}

const isEqual = (
  prevProps: { value: Item | null; isDisabled?: boolean; hideLabel?: boolean },
  nextProps: { value: Item | null; isDisabled?: boolean; hideLabel?: boolean }
) => {
  const prevId = prevProps.value?.id
  const nextId = nextProps.value?.id
  return (
    prevId === nextId &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.hideLabel === nextProps.hideLabel
  )
}

export default memo(FormMemberSelect, isEqual)
